import { notifications } from '@mantine/notifications';
import { captureException } from '@sentry/react';
import {
  type Mutation,
  MutationCache,
  QueryCache,
  QueryClient as QueryClientV5,
} from '@tanstack/react-query';
import Axios from 'axios';
import { isNullish } from 'remeda';
import type { ApiError, ApiResponse } from 'shared/hooks/utils';
import { redirectToLogout } from 'shared/utils/redirect-to-logout';
import { SilentError } from './silent-error';

export const queryClientV5 = new QueryClientV5({
  queryCache: new QueryCache({
    onError: handleError,
  }),
  mutationCache: new MutationCache({
    onSuccess: handleSuccess,
    onError: handleError,
  }),
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    },
  },
});

function handleSuccess(
  data: unknown,
  _variables: unknown,
  _context: unknown,
  mutation: Mutation<unknown, unknown, unknown>,
) {
  if (mutation.meta?.suppressSuccessNotification) return;

  const response = data as ApiResponse;
  if (!response.data?.message) return;

  notifications.show({
    color: 'green',
    message: response.data.message,
  });
}

function handleError(_error: unknown) {
  const error = _error as ApiError;

  // I really don't want errors to break my error-handling code
  try {
    console.error(error);

    if (error instanceof SilentError) return;

    // A 401 means the session has expired or been revoked server-side. It is
    // expected traffic, not an unhandled bug, so send the user to log back in
    // instead of reporting it to Sentry and leaving them stuck on a red toast.
    if (Axios.isAxiosError(error) && error.response?.status === 401) {
      redirectToLogout();
      return;
    }

    // A network error (offline, DNS failure, CORS, a timeout, or an aborted
    // request) never reaches the server, so it carries no stacktrace and no
    // status code to act on. It's expected traffic from the client's own
    // conditions, not an unhandled bug, so skip Sentry but still toast below.
    // Some browsers settle the request with a response object carrying
    // status 0 instead of leaving `response` unset (RECOVR-FRONT-2YD) — 0 is
    // never a real HTTP status, so it's the same unreportable case.
    //
    // The mail composer autosaves the open draft on a debounce. If the draft
    // was already sent or deleted in the meantime (e.g. from another tab),
    // that PUT settles 404 against a client-side draftId that's gone stale.
    // useSaveDraft already recovers from this (creates a fresh draft or
    // refetches the thread), so it carries no actionable signal for
    // engineers and the red toast below would only alarm the user over a
    // background save they never asked for. It was also flooding Sentry
    // with one-off issues (RECOVR-FRONT-2YN, -2YM, -2YK, -2YA, ...) because
    // the per-URL fingerprint below includes the draft id, so every
    // occurrence formed its own never-resolving issue instead of grouping.
    if (isDraftAutosaveRaceError(error)) return;

    if (isUnknownTenantError(error)) return;

    if (
      !isUnreportableNetworkError(error) &&
      !isOutgoingMailValidationError(error) &&
      !isPermissionDeniedError(error) &&
      !isFetchAddressValidationError(error) &&
      !isMissingDebtorError(error)
    ) {
      const sentryContext = axiosSentryContext(error);
      if (sentryContext) {
        captureException(error, sentryContext);
      } else {
        captureException(error);
      }
    }

    if (error.message.includes('Missing Refresh Token')) return;

    notifications.show({
      color: 'red',
      message:
        error.response?.data.errorMessage ??
        error.response?.data.error_message ??
        error.response?.data.errors ??
        error.message,
    });
  } catch (e) {
    console.error('Error handling failed:', e);
  }
}

function isUnreportableNetworkError(error: ApiError): boolean {
  if (!Axios.isAxiosError(error)) return false;

  return isNullish(error.response) || error.response.status === 0;
}

function isPermissionDeniedError(error: ApiError): boolean {
  return Axios.isAxiosError(error) && error.response?.status === 403;
}

function isDraftAutosaveRaceError(error: ApiError): boolean {
  if (!Axios.isAxiosError(error)) return false;

  return (
    error.response?.status === 404 &&
    error.config?.method?.toLowerCase() === 'put' &&
    /\/mails\/drafts\//.test(error.config?.url ?? '')
  );
}

function isUnknownTenantError(error: ApiError): boolean {
  if (!Axios.isAxiosError(error)) return false;

  return (
    error.response?.status === 404 &&
    error.config?.method?.toLowerCase() === 'get' &&
    /^\/?tenant\?/.test(error.config?.url ?? '')
  );
}

function isMissingDebtorError(error: ApiError): boolean {
  if (!Axios.isAxiosError(error)) return false;

  return (
    error.response?.status === 404 &&
    error.config?.method?.toLowerCase() === 'get' &&
    /(^|\/)debtors\/[^/]+(\/ai_summary)?$/.test(error.config?.url ?? '')
  );
}

function isOutgoingMailValidationError(error: ApiError): boolean {
  if (!Axios.isAxiosError(error)) return false;

  return (
    error.response?.status === 400 &&
    error.config?.method?.toLowerCase() === 'post' &&
    error.config?.url === 'mails' &&
    typeof error.response?.data?.error_message === 'string'
  );
}

// Axios errors carry no stack trace and always share the same generic message
// ("AxiosError: Request failed with status code N"), so Sentry groups every failing
// endpoint on a given page into one issue, making the real regressions untriageable.
// Fingerprinting by method/url/status splits them back into distinct issues.
function axiosSentryContext(error: ApiError) {
  if (!Axios.isAxiosError(error)) return undefined;

  const method = error.config?.method?.toUpperCase();
  const url = error.config?.url;
  const status = error.response?.status;

  return {
    fingerprint: ['{{ default }}', method ?? '', url ?? '', String(status ?? '')],
    tags: { http_method: method, http_status: status },
    extra: { http_url: url, response_data: error.response?.data },
  };
}

function isFetchAddressValidationError(error: ApiError): boolean {
  if (!Axios.isAxiosError(error)) return false;

  return (
    error.response?.status === 422 &&
    error.config?.method?.toLowerCase() === 'post' &&
    error.config?.url === 'fetch_address' &&
    typeof error.response?.data?.error_message === 'string'
  );
}
